import React, { useState, useEffect } from 'react';
import {
    Container,
    Box,
    Typography,
    Paper,
    Button,
    Alert,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Tooltip,
    Card,
    CardContent,
    Grid
} from '@mui/material';
import {
    PlayArrow as PlayIcon,
    Refresh as RefreshIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Warning as WarningIcon,
    Info as InfoIcon
} from '@mui/icons-material';
import { transactionProcessorAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

interface ProcessingLog {
    id: string;
    status: string;
    startedAt: string;
    completedAt: string;
    recurringTransactionsCreated: number;
    transactionsProcessed: number;
    transactionsSucceeded: number;
    transactionsFailed: number;
    errorMessage?: string;
    triggeredBy: string;
    triggeredByUser?: string;
}

interface ProcessingResult {
    success: boolean;
    message: string;
    summary: {
        tenantsProcessed: number;
        recurringTransactionsCreated: number;
        transactionsProcessed: number;
        transactionsSucceeded: number;
        transactionsFailed: number;
        duration: string;
        triggeredBy: string;
        triggeredByUser?: string;
    };
    errors?: string[];
}

const TransactionProcessor: React.FC = () => {
    const { user } = useAuth();
    const [processing, setProcessing] = useState(false);
    const [logs, setLogs] = useState<ProcessingLog[]>([]);
    const [loadingLogs, setLoadingLogs] = useState(false);
    const [result, setResult] = useState<ProcessingResult | null>(null);
    const [error, setError] = useState<string | null>(null);

    // Only allow SUPER_ADMIN
    const isSuperAdmin = user?.role === 'super_admin';

    useEffect(() => {
        if (isSuperAdmin) {
            fetchLogs();
        }
    }, [isSuperAdmin]);

    const fetchLogs = async () => {
        setLoadingLogs(true);
        try {
            const response = await transactionProcessorAPI.getLogs(20);
            setLogs(response.data.logs || []);
        } catch (err: any) {
            console.error('Error fetching processing logs:', err);
        } finally {
            setLoadingLogs(false);
        }
    };

    const handleProcessTransactions = async () => {
        setProcessing(true);
        setError(null);
        setResult(null);

        try {
            const response = await transactionProcessorAPI.process();
            setResult(response.data);

            // Refresh logs after processing
            setTimeout(() => {
                fetchLogs();
            }, 1000);
        } catch (err: any) {
            console.error('Error processing transactions:', err);
            const errorMsg = err.response?.data?.message || err.message || 'Failed to process transactions';
            setError(errorMsg);
        } finally {
            setProcessing(false);
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status.toUpperCase()) {
            case 'SUCCESS':
                return <CheckCircleIcon color="success" />;
            case 'PARTIAL_SUCCESS':
                return <WarningIcon color="warning" />;
            case 'FAILED':
                return <ErrorIcon color="error" />;
            case 'RUNNING':
                return <CircularProgress size={20} />;
            default:
                return <InfoIcon color="info" />;
        }
    };

    const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'info' | 'default' => {
        switch (status.toUpperCase()) {
            case 'SUCCESS':
                return 'success';
            case 'PARTIAL_SUCCESS':
                return 'warning';
            case 'FAILED':
                return 'error';
            case 'RUNNING':
                return 'info';
            default:
                return 'default';
        }
    };

    const formatDateTime = (dateString: string) => {
        return new Date(dateString).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    if (!isSuperAdmin) {
        return (
            <Container maxWidth="lg">
                <Box sx={{ mt: 4 }}>
                    <Alert severity="error">
                        Access Denied: Only Super Administrators can access this page.
                    </Alert>
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl">
            <Box sx={{ mt: 4, mb: 4 }}>
                <Typography variant="h4" gutterBottom>
                    Transaction Processor
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                    Manually trigger transaction processing or view processing history. This runs the same process as the scheduled cron job.
                </Typography>

                {/* Manual Trigger Section */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Box>
                            <Typography variant="h6" gutterBottom>
                                Manual Processing
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Process recurring transactions and pending payments across all active tenants
                            </Typography>
                        </Box>
                        <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            startIcon={processing ? <CircularProgress size={20} color="inherit" /> : <PlayIcon />}
                            onClick={handleProcessTransactions}
                            disabled={processing}
                        >
                            {processing ? 'Processing...' : 'Run Transaction Processor'}
                        </Button>
                    </Box>

                    {/* Processing Result */}
                    {result && (
                        <Box sx={{ mt: 3 }}>
                            <Alert
                                severity={result.success ? 'success' : 'warning'}
                                sx={{ mb: 2 }}
                            >
                                {result.message}
                            </Alert>

                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                    <Card>
                                        <CardContent>
                                            <Typography color="text.secondary" gutterBottom>
                                                Tenants Processed
                                            </Typography>
                                            <Typography variant="h4">
                                                {result.summary.tenantsProcessed}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                    <Card>
                                        <CardContent>
                                            <Typography color="text.secondary" gutterBottom>
                                                Recurring Created
                                            </Typography>
                                            <Typography variant="h4">
                                                {result.summary.recurringTransactionsCreated}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                    <Card>
                                        <CardContent>
                                            <Typography color="text.secondary" gutterBottom>
                                                Transactions Processed
                                            </Typography>
                                            <Typography variant="h4">
                                                {result.summary.transactionsProcessed}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                    <Card>
                                        <CardContent>
                                            <Typography color="text.secondary" gutterBottom>
                                                Succeeded
                                            </Typography>
                                            <Typography variant="h4" color="success.main">
                                                {result.summary.transactionsSucceeded}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                    <Card>
                                        <CardContent>
                                            <Typography color="text.secondary" gutterBottom>
                                                Failed
                                            </Typography>
                                            <Typography variant="h4" color="error.main">
                                                {result.summary.transactionsFailed}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                    <Card>
                                        <CardContent>
                                            <Typography color="text.secondary" gutterBottom>
                                                Duration
                                            </Typography>
                                            <Typography variant="h4">
                                                {result.summary.duration}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>

                            {result.errors && result.errors.length > 0 && (
                                <Box sx={{ mt: 2 }}>
                                    <Alert severity="error">
                                        <Typography variant="subtitle2" gutterBottom>
                                            Errors ({result.errors.length}):
                                        </Typography>
                                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                                            {result.errors.map((error, index) => (
                                                <li key={index}><Typography variant="body2">{error}</Typography></li>
                                            ))}
                                        </ul>
                                    </Alert>
                                </Box>
                            )}
                        </Box>
                    )}

                    {error && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {error}
                        </Alert>
                    )}
                </Paper>

                {/* Processing History */}
                <Paper sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">
                            Processing History
                        </Typography>
                        <Tooltip title="Refresh">
                            <IconButton onClick={fetchLogs} disabled={loadingLogs}>
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>

                    {loadingLogs ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : logs.length === 0 ? (
                        <Alert severity="info">
                            No processing history available
                        </Alert>
                    ) : (
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Started</TableCell>
                                        <TableCell>Completed</TableCell>
                                        <TableCell align="center">Recurring Created</TableCell>
                                        <TableCell align="center">Processed</TableCell>
                                        <TableCell align="center">Succeeded</TableCell>
                                        <TableCell align="center">Failed</TableCell>
                                        <TableCell>Triggered By</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {logs.map((log) => (
                                        <TableRow key={log.id} hover>
                                            <TableCell>
                                                <Chip
                                                    icon={getStatusIcon(log.status)}
                                                    label={log.status.replace('_', ' ')}
                                                    color={getStatusColor(log.status)}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>{formatDateTime(log.startedAt)}</TableCell>
                                            <TableCell>
                                                {log.completedAt ? formatDateTime(log.completedAt) : '-'}
                                            </TableCell>
                                            <TableCell align="center">
                                                {log.recurringTransactionsCreated}
                                            </TableCell>
                                            <TableCell align="center">
                                                {log.transactionsProcessed}
                                            </TableCell>
                                            <TableCell align="center">
                                                <Typography color="success.main">
                                                    {log.transactionsSucceeded}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                {log.transactionsFailed > 0 ? (
                                                    <Tooltip title={log.errorMessage || 'View details'}>
                                                        <Typography color="error.main">
                                                            {log.transactionsFailed}
                                                        </Typography>
                                                    </Tooltip>
                                                ) : (
                                                    <Typography>{log.transactionsFailed}</Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Box>
                                                    <Chip
                                                        label={log.triggeredBy}
                                                        size="small"
                                                        color={log.triggeredBy === 'MANUAL' ? 'primary' : 'default'}
                                                    />
                                                    {log.triggeredByUser && (
                                                        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                                            {log.triggeredByUser}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Paper>
            </Box>
        </Container>
    );
};

export default TransactionProcessor;
